import React from "react";
import type { User } from "../../types/auth";
import { Edit2 } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

interface UserTableProps {
  users: User[];
  actionLoadingId?: string | null;
  onEdit: (user: User) => void;
  onActivate: (user: User) => void;
  onDeactivate: (user: User) => void;
  onResetPassword: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  actionLoadingId,
  onEdit,
  onActivate,
  onDeactivate,
  onResetPassword,
}) => {
  const { user: currentUser } = useAuth();

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        No users found
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Email
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Role
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Fleet
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Last login
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Active
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => {
            const isSelf = user.id === currentUser?.id;
            const isLoading = actionLoadingId === user.id;

            return (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {user.full_name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {user.email}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                  {user.role}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {user.fleet_name ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {user.last_login
                    ? new Date(user.last_login).toLocaleString()
                    : "Never"}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      user.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {user.is_active ? "Yes" : "No"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {isSelf ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => onEdit(user)}
                        disabled={isLoading}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        title="Edit user"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {user.is_active ? (
                        <button
                          type="button"
                          onClick={() => onDeactivate(user)}
                          disabled={isLoading}
                          className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onActivate(user)}
                          disabled={isLoading}
                          className="text-xs px-2 py-1 border border-green-200 text-green-700 rounded hover:bg-green-50 disabled:opacity-50"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onResetPassword(user)}
                        disabled={isLoading}
                        className="text-xs px-2 py-1 border border-amber-200 text-amber-800 rounded hover:bg-amber-50 disabled:opacity-50"
                      >
                        Reset password
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
